
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { FileText, Layers, Tag, DollarSign, Users, Grid3X3, Plus, FileSearch } from "lucide-react";
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle
} from "@/components/ui/navigation-menu";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/components/ThemeProvider";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";

interface NavigationListItemProps extends React.ComponentPropsWithoutRef<"a"> {
  title: string;
}

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  NavigationListItemProps
>(({ className, title, children, ...props }, ref) => {
  const { theme } = useTheme();
  const is24Seven = theme === "24seven";
  
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors",
            is24Seven
              ? "hover:bg-gray-100 hover:text-24seven-text focus:bg-gray-100 focus:text-24seven-text"
              : "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className
          )}
          {...props}
        >
          <div className={cn(
            "text-sm font-medium leading-none",
            is24Seven && "text-24seven-text"
          )}>
            {title}
          </div>
          <div className={cn(
            "line-clamp-2 text-sm leading-snug",
            is24Seven ? "text-24seven-muted" : "text-muted-foreground"
          )}>
            {children}
          </div>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

export function DesktopNavigation() {
  const location = useLocation();
  const currentPath = location.pathname;
  const { theme } = useTheme();
  const is24Seven = theme === "24seven";
  const { canUseDocumentExtraction } = useFeatureAccess();
  
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };
  
  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger 
            className={cn(
              is24Seven
                ? isActive("/contracts") ? "bg-24seven-navy/10 text-24seven-text" : "text-24seven-text"
                : isActive("/contracts") ? "bg-accent/50" : ""
            )}
          >
            <FileText className="mr-2 h-4 w-4" />
            Contracts
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid gap-3 p-4 w-[400px] md:w-[500px] lg:w-[600px] grid-cols-2">
              <ListItem
                title="All Contracts"
                href="/contracts"
              >
                Browse and manage all your contracts in one place
              </ListItem>
              <ListItem
                title="Quick Add Contract"
                href={`/contracts/quick-add?returnTo=${encodeURIComponent(currentPath)}`}
              >
                <div className="flex items-center">
                  Create a contract quickly
                  <Badge 
                    className={cn(
                      "ml-2",
                      is24Seven 
                        ? "bg-24seven-accent/20 text-24seven-accent hover:bg-24seven-accent/30" 
                        : "bg-primary/20 text-primary hover:bg-primary/30"
                    )} 
                    variant="outline"
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Fast
                  </Badge>
                </div>
              </ListItem>
              <ListItem
                title="Add New Contract"
                href="/contracts/new"
              >
                <div className="flex items-center">
                  Create a new contract with all details
                  <Badge 
                    className={cn(
                      "ml-2",
                      is24Seven 
                        ? "bg-24seven-accent/20 text-24seven-accent hover:bg-24seven-accent/30" 
                        : "bg-primary/20 text-primary hover:bg-primary/30"
                    )} 
                    variant="outline"
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Full
                  </Badge>
                </div>
              </ListItem>
              {canUseDocumentExtraction && (
                <li>
                  <NavigationMenuLink asChild>
                    <div
                      className={cn(
                        "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors opacity-50 cursor-not-allowed",
                        is24Seven
                          ? "bg-gray-50 text-24seven-muted"
                          : "bg-muted/30 text-muted-foreground"
                      )}
                    >
                      <div className={cn(
                        "text-sm font-medium leading-none",
                        is24Seven && "text-24seven-muted"
                      )}>
                        Extract from Documents
                      </div>
                      <div className={cn(
                        "line-clamp-2 text-sm leading-snug flex items-center justify-between",
                        is24Seven ? "text-24seven-muted" : "text-muted-foreground"
                      )}>
                        <span>Extract contract details from PDF documents</span>
                        <Badge 
                          className={cn(
                            "ml-2",
                            is24Seven 
                              ? "bg-orange-100 text-orange-600 hover:bg-orange-100" 
                              : "bg-orange-100 text-orange-600 hover:bg-orange-100"
                          )} 
                          variant="outline"
                        >
                          In Development
                        </Badge>
                      </div>
                    </div>
                  </NavigationMenuLink>
                </li>
              )}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
        
        <NavigationMenuItem>
          <NavigationMenuTrigger 
            className={cn(
              is24Seven
                ? (isActive("/categories") || 
                   isActive("/contract-types") || 
                   isActive("/currencies") || 
                   isActive("/contacts") || 
                   isActive("/groups")) ? "bg-24seven-navy/10 text-24seven-text" : "text-24seven-text"
                : (isActive("/categories") || 
                   isActive("/contract-types") || 
                   isActive("/currencies") || 
                   isActive("/contacts") || 
                   isActive("/groups")) ? "bg-accent/50" : ""
            )}
          >
            <Layers className="mr-2 h-4 w-4" />
            Manage...
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid gap-3 p-4 w-[400px] md:w-[500px] lg:w-[600px] grid-cols-2">
              <li className="row-span-3">
                <NavigationMenuLink asChild>
                  <a
                    className={cn(
                      "flex h-full w-full select-none flex-col justify-end rounded-md p-6 no-underline outline-none focus:shadow-md",
                      is24Seven 
                        ? "bg-gradient-to-b from-24seven-light to-gray-100" 
                        : "bg-gradient-to-b from-muted/50 to-muted"
                    )}
                    href="/categories"
                  >
                    <Layers className={cn("h-6 w-6", is24Seven && "text-24seven-navy")} />
                    <div className={cn(
                      "mb-2 mt-4 text-lg font-medium",
                      is24Seven && "text-24seven-text"
                    )}>
                      Manage...
                    </div>
                    <p className={cn(
                      "text-sm leading-tight",
                      is24Seven ? "text-24seven-muted" : "text-muted-foreground"
                    )}>
                      Manage your categories, contract types, currencies, contacts, and groups
                    </p>
                  </a>
                </NavigationMenuLink>
              </li>
              
              <ListItem
                href="/categories"
                title="Categories"
              >
                <div className="flex items-center">
                  <Tag className="mr-2 h-4 w-4" />
                  Manage contract categorization
                </div>
              </ListItem>
              
              <ListItem
                href="/contract-types"
                title="Contract Types"
              >
                <div className="flex items-center">
                  <FileText className="mr-2 h-4 w-4" />
                  Define different types of contracts
                </div>
              </ListItem>
              
              <ListItem
                href="/currencies"
                title="Currencies"
              >
                <div className="flex items-center">
                  <DollarSign className="mr-2 h-4 w-4" />
                  Set up currencies and exchange rates
                </div>
              </ListItem>
              
              <ListItem
                href="/contacts"
                title="Contacts"
              >
                <div className="flex items-center">
                  <Users className="mr-2 h-4 w-4" />
                  Manage contract contacts
                </div>
              </ListItem>
              
              <ListItem
                href="/groups"
                title="Groups"
              >
                <div className="flex items-center">
                  <Grid3X3 className="mr-2 h-4 w-4" />
                  Organize contracts into groups
                </div>
              </ListItem>
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
